import { z } from "zod";
import {
  AmountDeltaSchema,
  DateRangeSchema,
  DiscountAppliedSchema,
  GuaranteeAppliedEnum,
  GuaranteeAppliedEnumSchema,
  NotificationChannelEnum,
  NotificationChannelEnumSchema,
  PenaltyAppliedEnum,
  PenaltyAppliedEnumSchema,
  SellingConfigurationTypeEnum,
  SellingConfigurationTypeEnumSchema,
  SellingLocationEnum,
  SellingLocationEnumSchema,
  UpgradeTargetSchema,
} from "../domain/upgradeConfiguration.schema";

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email);
}

export function hasBothDates(rule: {
  includedDates?: unknown[];
  excludedDates?: unknown[];
}): boolean {
  return (rule?.includedDates?.length ?? 0) > 0 && (rule?.excludedDates?.length ?? 0) > 0;
}

// Form-specific schema for validation (more lenient for form editing)
export const UpgradeConfigurationFormSchema = z.object({
  id: z.string().optional(),
  active: z.boolean().default(false),
  propertyTicker: z.string().min(1, "Property ticker is required"),
  propertyRecipients: z.array(z.string()).optional().default([]),
  sellingLocations: z.array(SellingLocationEnumSchema).optional().default([]),
  notificationChannels: z.array(NotificationChannelEnumSchema).optional().default([]),
  sellingConfigurationType: SellingConfigurationTypeEnumSchema.optional().default(
    SellingConfigurationTypeEnum.UPSELLING
  ),
  generalSellingActivationRule: z.object({
    maxReleaseInDays: z.number().min(0, "Max release must be 0 or greater"),
    minReleaseInDays: z.number().min(0, "Min release must be 0 or greater").optional(),
    minAmountDelta: AmountDeltaSchema.optional(),
    maxAmountDelta: AmountDeltaSchema.optional(),
    minStayInDays: z.number().min(0).optional(),
    maxStayInDays: z.number().min(0).optional(),
    minSourceAvailability: z.number().min(0).optional(),
    maxSourceAvailability: z.number().min(0).optional(),
    minUpgradeToAvailability: z.number().min(0).optional(),
    maxUpgradeToAvailability: z.number().min(0).optional(),
    excludedDates: z.array(DateRangeSchema).optional().default([]),
    includedDates: z.array(DateRangeSchema).optional().default([]),
    penaltyApplied: PenaltyAppliedEnumSchema.optional(),
    guaranteeApplied: GuaranteeAppliedEnumSchema.optional(),
    includedTags: z.array(z.string()).optional().default([]),
    promoCodes: z.array(z.string()).optional().default([]),
  }),
  sellingTargets: z.array(UpgradeTargetSchema).optional().default([]),
  discountApplied: DiscountAppliedSchema.optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
});

export type UpgradeConfigurationFormInput = z.input<typeof UpgradeConfigurationFormSchema>;
export type UpgradeConfigurationFormType = z.output<typeof UpgradeConfigurationFormSchema>;

// Default values for the form
export const defaultUpgradeConfigurationValues: Partial<UpgradeConfigurationFormInput> = {
  active: false,
  propertyRecipients: [],
  sellingLocations: [SellingLocationEnum.NOTIFICATION],
  notificationChannels: [NotificationChannelEnum.EMAIL],
  sellingConfigurationType: SellingConfigurationTypeEnum.UPSELLING,
  generalSellingActivationRule: {
    maxReleaseInDays: 0,
    minReleaseInDays: undefined,
    minAmountDelta: undefined,
    maxAmountDelta: undefined,
    minStayInDays: undefined,
    maxStayInDays: undefined,
    minSourceAvailability: undefined,
    maxSourceAvailability: undefined,
    minUpgradeToAvailability: undefined,
    maxUpgradeToAvailability: undefined,
    excludedDates: [],
    includedDates: [],
    penaltyApplied: PenaltyAppliedEnum.IGNORE,
    guaranteeApplied: GuaranteeAppliedEnum.IGNORE,
    includedTags: [],
    promoCodes: [],
  },
  sellingTargets: [],
};
